import { formatMoney } from '@theme/utilities';
import { ToggleCrossSellsUpdatedEvent } from '@theme/events';

const MAX_CROSS_SELL_QTY = 999;

/**
 * Centimes unitaires après réduction % (arithmétique entière — évite les erreurs flottantes type 39,87 au lieu de 39,88 €).
 */
function centsAfterPercentReduction(unitPriceCents, reductionPercent) {
  const up = parseInt(String(unitPriceCents), 10) || 0;
  const r = Math.min(100, Math.max(0, parseInt(String(reductionPercent), 10) || 0));

  return Math.round((up * (100 - r)) / 100);
}

/**
 * Prix affiché / total ligne Toggle pour une offre palier (réduction % sur les unités couvertes par bundleMax).
 */
function getToggleOfferLineTotals(qty, bundleMax, unitPrice, unitCompare, reductionPercent) {
  const b = parseInt(bundleMax, 10) || 0;
  const q = parseInt(qty, 10) || 0;
  const r = Math.min(100, Math.max(0, parseInt(String(reductionPercent), 10) || 0));

  const up = parseInt(String(unitPrice), 10) || 0;
  const uc = parseInt(String(unitCompare), 10) || up;

  if (b <= 0) {
    return { price: up * q, compareAtPrice: uc * q };
  }
  if (q < b) {
    return { price: up * q, compareAtPrice: uc * q };
  }

  const extra = q - b;
  const discountedCents = Math.round((up * b * (100 - r)) / 100);
  const price = discountedCents + up * extra;
  const compareAtPrice = uc * q;

  return { price, compareAtPrice };
}

/**
 * Variante affichée sur une ligne Toggle (sélecteur, case, bouton).
 * @param {Element} row
 * @returns {string | null}
 */
function getToggleCrossSellRowVariantId(row) {
  const select = row.querySelector('[data-ref="toggle-cross-sell-select"]');
  if (select) {
    const v = select.value;
    return v != null && v !== '' ? String(v) : null;
  }

  const checkbox = row.querySelector('[data-ref="toggle-cross-sell-checkbox"]');
  if (checkbox && checkbox.dataset.variantId) {
    return String(checkbox.dataset.variantId);
  }

  const btn = row.querySelector('[data-ref="toggle-cross-sell-button"]');
  if (btn && btn.dataset.variantId) {
    return String(btn.dataset.variantId);
  }

  return null;
}

class ToggleCrossSellList extends HTMLElement {
  constructor() {
    super();

    this.crossSells = null;
  }

  connectedCallback() {
    this.crossSells = this.querySelectorAll('[data-ref="toggle-cross-sell"]');
  }

  /**
   * Aperçu « palier suivant » : tout est lu depuis le DOM (palier sélectionné + lignes incluses Liquid).
   * Pas de paramètres ni totaux JS à réconcilier — évite les désynchronisations après ajout/retrait Toggle.
   */
  applyNextTierPreviews() {
    const form = this.closest('product-form');
    const qb = form?.querySelector('[data-ref="quantity-breaks"]');
    if (!qb) return;

    const items = [...qb.querySelectorAll('[data-ref="quantity-break-item"]')];
    const idx = items.findIndex((el) => el.classList.contains('quantity-break--selected'));
    if (idx < 0) return;

    const tierSelectedEl = items[idx];
    const rows = this.querySelectorAll('[data-ref="toggle-cross-sell"]');

    rows.forEach((row) => {
      row.clearNextTierOfferPreview?.({ skipRefresh: true });
    });

    rows.forEach((row) => {
      const vid = getToggleCrossSellRowVariantId(row);
      if (!vid) {
        row.refreshMarginalPriceDisplay?.();
        return;
      }

      const inCurrent = [...tierSelectedEl.querySelectorAll('[data-ref="quantity-break-included-item"]')].some(
        (s) => String(s.dataset.variantId) === vid
      );

      if (inCurrent) {
        row.refreshMarginalPriceDisplay?.();
        return;
      }

      let futureSpan = null;
      for (let i = idx + 1; i < items.length; i++) {
        const span = [...items[i].querySelectorAll('[data-ref="quantity-break-included-item"]')].find(
          (s) =>
            String(s.dataset.variantId) === vid && s.hasAttribute('data-included-price-reduction-percent')
        );
        if (span) {
          const rp = parseInt(span.getAttribute('data-included-price-reduction-percent') ?? '', 10);
          /* Pas d’aperçu « offert » (100 %) depuis un palier futur : le vrai 0 € / badge n’a lieu que sur le palier sélectionné qui inclut la ligne. Les rabais partiels (ex. 75 %) restent prévisualisables. */
          if (!Number.isNaN(rp) && rp < 100) {
            futureSpan = span;
          }
          break;
        }
      }

      const unit = futureSpan ? parseInt(futureSpan.dataset.price, 10) : NaN;
      if (futureSpan && !Number.isNaN(unit) && typeof row.setNextTierOfferPreview === 'function') {
        row.setNextTierOfferPreview(futureSpan);
      } else {
        row.refreshMarginalPriceDisplay?.();
      }
    });

    document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
  }

  /**
   * Aligne les lignes Toggle sur les produits inclus du palier (ex. 2 coussins comme si le client les avait pris ici).
   * @param {Map<string, number> | Record<string, number>} includedByVariant — variantId → quantité incluse dans l’offre
   */
  syncIncludedQuantitiesFromQuantityBreak(includedByVariant) {
    const map =
      includedByVariant instanceof Map ? includedByVariant : new Map(Object.entries(includedByVariant || {}));

    const rows = this.querySelectorAll('[data-ref="toggle-cross-sell"]');
    const syncOpts = { skipPriceRefresh: true };

    if (map.size === 0) {
      rows.forEach((crossSellEl) => {
        crossSellEl.applyQuantityFromQuantityBreakOffer?.(0, 0, undefined, syncOpts);
      });
      return;
    }

    rows.forEach((crossSellEl) => {
      const select = crossSellEl.querySelector('[data-ref="toggle-cross-sell-select"]');
      let matchedVid = null;

      for (const vid of map.keys()) {
        const vidStr = String(vid);

        if (select) {
          if (select.querySelector(`option[value="${vidStr}"]`)) {
            matchedVid = vidStr;
            break;
          }
        } else {
          const btn = crossSellEl.querySelector('[data-ref="toggle-cross-sell-button"]');
          if (btn && String(btn.dataset.variantId) === vidStr) {
            matchedVid = vidStr;
            break;
          }
        }
      }

      if (matchedVid == null) {
        crossSellEl.applyQuantityFromQuantityBreakOffer?.(0, 0, undefined, syncOpts);
        return;
      }

      const entry = map.get(matchedVid);
      const qty = entry != null && typeof entry === 'object' && 'qty' in entry ? entry.qty : entry;
      const reductionPercent =
        entry != null && typeof entry === 'object' && 'reductionPercent' in entry ? entry.reductionPercent : 100;

      if (qty === undefined || qty < 1) {
        crossSellEl.applyQuantityFromQuantityBreakOffer?.(0, 0, undefined, syncOpts);
        return;
      }

      if (select && String(select.value) !== String(matchedVid)) {
        select.value = matchedVid;
        const opt = select.options[select.selectedIndex];
        if (opt && typeof crossSellEl.updateProductPrice === 'function') {
          crossSellEl.updateProductPrice(opt);
          crossSellEl.updateProductImage(opt);
          crossSellEl.updateVariantId(opt);
        }
      }

      crossSellEl.applyQuantityFromQuantityBreakOffer?.(qty, qty, reductionPercent, syncOpts);
    });
  }

  getSelectedItems() {
    let items = [];
    let total_price = 0;
    let total_compare_at_price = 0;

    this.crossSells.forEach((crossSell) => {
      const checkbox = crossSell.querySelector('[data-ref="toggle-cross-sell-checkbox"]');
      const button = crossSell.querySelector('[data-ref="toggle-cross-sell-button"]');

      if (checkbox != null && checkbox.checked == true) {
        items.push({
          id: checkbox.dataset.variantId,
          quantity: 1,
        });

        const unitPrice = parseInt(crossSell.getPrices().price, 10);
        const unitCompare = parseInt(crossSell.getPrices().compareAtPrice, 10);
        const bundleMax = parseInt(crossSell.dataset.offerBundleMaxQty || '0', 10);
        const reduction = parseInt(crossSell.dataset.offerIncludedReductionPercent ?? '100', 10);
        const lineTotals = getToggleOfferLineTotals(1, bundleMax, unitPrice, unitCompare, reduction);

        total_price += lineTotals.price;
        total_compare_at_price += lineTotals.compareAtPrice;
      }

      if (button == null) return;

      if (crossSell.dataset.quantitySelector === 'true') {
        const qty = parseInt(crossSell.dataset.selectedQuantity || '0', 10);
        if (qty > 0) {
          items.push({
            id: button.dataset.variantId,
            quantity: qty,
          });

          const unitPrice = parseInt(crossSell.getPrices().price, 10);
          const unitCompare = parseInt(crossSell.getPrices().compareAtPrice, 10);
          const bundleMax = parseInt(crossSell.dataset.offerBundleMaxQty || '0', 10);
          const reduction = parseInt(crossSell.dataset.offerIncludedReductionPercent ?? '100', 10);
          const lineTotals = getToggleOfferLineTotals(qty, bundleMax, unitPrice, unitCompare, reduction);

          total_price += lineTotals.price;
          total_compare_at_price += lineTotals.compareAtPrice;
        }
        return;
      }

      if (button.classList.contains('button--active') == true) {
        const lineQty = parseInt(crossSell.dataset.syncedOfferQuantity || '1', 10) || 1;
        items.push({
          id: button.dataset.variantId,
          quantity: lineQty,
        });

        const unitPrice = parseInt(crossSell.getPrices().price, 10);
        const unitCompare = parseInt(crossSell.getPrices().compareAtPrice, 10);
        const bundleMax = parseInt(crossSell.dataset.offerBundleMaxQty || '0', 10);
        const reduction = parseInt(crossSell.dataset.offerIncludedReductionPercent ?? '100', 10);
        const lineTotals = getToggleOfferLineTotals(lineQty, bundleMax, unitPrice, unitCompare, reduction);

        total_price += lineTotals.price;
        total_compare_at_price += lineTotals.compareAtPrice;
      }
    });

    return { items: items, total_price: total_price, total_compare_at_price: total_compare_at_price };
  }

  /**
   * Totaux Toggle à ajouter au prix du bouton : hors partie déjà comprise dans le palier QB (produits inclus).
   * @param {HTMLElement | null} quantityBreaks — élément quantity-breaks avec getIncludedCentContributionForVariant, ou null
   */
  getProductFormAddonTotals(quantityBreaks) {
    const getContrib = (variantId) => {
      if (
        quantityBreaks &&
        typeof quantityBreaks.getIncludedCentContributionForVariant === 'function'
      ) {
        return quantityBreaks.getIncludedCentContributionForVariant(variantId);
      }

      return { price: 0, compareAtPrice: 0 };
    };

    let total_price = 0;
    let total_compare_at_price = 0;

    this.crossSells.forEach((crossSell) => {
      const checkbox = crossSell.querySelector('[data-ref="toggle-cross-sell-checkbox"]');
      const button = crossSell.querySelector('[data-ref="toggle-cross-sell-button"]');

      if (checkbox != null && checkbox.checked == true) {
        const vid = checkbox.dataset.variantId;
        const c = getContrib(vid);
        const unitPrice = parseInt(crossSell.getPrices().price, 10);
        const unitCompare = parseInt(crossSell.getPrices().compareAtPrice, 10);
        const bundleMax = parseInt(crossSell.dataset.offerBundleMaxQty || '0', 10);
        const reduction = parseInt(crossSell.dataset.offerIncludedReductionPercent ?? '100', 10);
        const lineTotals = getToggleOfferLineTotals(1, bundleMax, unitPrice, unitCompare, reduction);

        total_price += Math.max(0, lineTotals.price - c.price);
        total_compare_at_price += Math.max(0, lineTotals.compareAtPrice - c.compareAtPrice);
      }

      if (button == null) return;

      if (crossSell.dataset.quantitySelector === 'true') {
        const qty = parseInt(crossSell.dataset.selectedQuantity || '0', 10);
        if (qty > 0) {
          const vid = button.dataset.variantId;
          const c = getContrib(vid);
          const unitPrice = parseInt(crossSell.getPrices().price, 10);
          const unitCompare = parseInt(crossSell.getPrices().compareAtPrice, 10);
          const bundleMax = parseInt(crossSell.dataset.offerBundleMaxQty || '0', 10);
          const reduction = parseInt(crossSell.dataset.offerIncludedReductionPercent ?? '100', 10);
          const lineTotals = getToggleOfferLineTotals(qty, bundleMax, unitPrice, unitCompare, reduction);

          total_price += Math.max(0, lineTotals.price - c.price);
          total_compare_at_price += Math.max(0, lineTotals.compareAtPrice - c.compareAtPrice);
        }
        return;
      }

      if (button.classList.contains('button--active') == true) {
        const lineQty = parseInt(crossSell.dataset.syncedOfferQuantity || '1', 10) || 1;
        const vid = button.dataset.variantId;
        const c = getContrib(vid);
        const unitPrice = parseInt(crossSell.getPrices().price, 10);
        const unitCompare = parseInt(crossSell.getPrices().compareAtPrice, 10);
        const bundleMax = parseInt(crossSell.dataset.offerBundleMaxQty || '0', 10);
        const reduction = parseInt(crossSell.dataset.offerIncludedReductionPercent ?? '100', 10);
        const lineTotals = getToggleOfferLineTotals(lineQty, bundleMax, unitPrice, unitCompare, reduction);

        total_price += Math.max(0, lineTotals.price - c.price);
        total_compare_at_price += Math.max(0, lineTotals.compareAtPrice - c.compareAtPrice);
      }
    });

    return { total_price, total_compare_at_price };
  }
}

if (!customElements.get('toggle-cross-sell-list')) {
  customElements.define('toggle-cross-sell-list', ToggleCrossSellList);
}

class ToggleCrossSell extends HTMLElement {
  constructor() {
    super();

    this.checkbox = null;
    this.button = null;
    this.select = null;
    this.image = null;
    this.price = null;
    this.quantitySelector = null;
    this.qtyMinus = null;
    this.qtyPlus = null;
    this.qtyValue = null;
  }

  connectedCallback() {
    this.checkbox = this.querySelector('[data-ref="toggle-cross-sell-checkbox"]');
    this.button = this.querySelector('[data-ref="toggle-cross-sell-button"]');
    this.select = this.querySelector('[data-ref="toggle-cross-sell-select"]');
    this.image = this.querySelector('[data-ref="toggle-cross-sell-image"]');
    this.price = this.querySelector('[data-ref="toggle-cross-sell-price"]');
    this.quantitySelector = this.querySelector('[data-ref="toggle-cross-sell-quantity-selector"]');
    this.qtyMinus = this.querySelector('[data-ref="toggle-cross-sell-qty-minus"]');
    this.qtyPlus = this.querySelector('[data-ref="toggle-cross-sell-qty-plus"]');
    this.qtyValue = this.querySelector('[data-ref="toggle-cross-sell-qty-value"]');

    this.select?.addEventListener('change', this.#onSelectChange);
    this.button?.addEventListener('click', this.#onButtonClick);
    this.checkbox?.addEventListener('change', this.checkboxChanged.bind(this));
    this.qtyMinus?.addEventListener('click', this.#onQtyMinus);
    this.qtyPlus?.addEventListener('click', this.#onQtyPlus);

    this.#updateSavingsBadge();
  }

  disconnectedCallback() {
    this.select?.removeEventListener('change', this.#onSelectChange);
    this.button?.removeEventListener('click', this.#onButtonClick);
    this.qtyMinus?.removeEventListener('click', this.#onQtyMinus);
    this.qtyPlus?.removeEventListener('click', this.#onQtyPlus);
  }

  #onSelectChange = () => {
    this.variantChanged();
  };

  #onButtonClick = () => {
    if (this.#isQuantitySelectorMode()) {
      this.#openQuantitySelector();
      return;
    }

    this.toggleAddButton();
  };

  #onQtyMinus = (event) => {
    event?.stopPropagation();

    const current = parseInt(this.dataset.selectedQuantity || '0', 10);
    if (current <= 1) {
      this.#setSelectedQuantity(0, { skipPriceRefresh: true });
      this.#syncQuantityBreaksAfterOfferLineRemoved();
      return;
    }

    this.#setSelectedQuantity(current - 1);
    document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
  };

  #onQtyPlus = (event) => {
    event?.stopPropagation();

    const current = parseInt(this.dataset.selectedQuantity || '0', 10);
    if (current < 1) return;

    this.#setSelectedQuantity(Math.min(MAX_CROSS_SELL_QTY, current + 1));
    document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
  };

  #isQuantitySelectorMode() {
    return this.dataset.quantitySelector === 'true';
  }

  #openQuantitySelector() {
    this.#promotePreviewOfferToBundleIfNeeded();
    this.button?.setAttribute('hidden', '');
    this.quantitySelector?.removeAttribute('hidden');
    this.#setSelectedQuantity(1);
    document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
  }

  /**
   * @param {number} quantity — quantité affichée / panier
   * @param {number} offerBundleQty — quantité max incluse dans l’offre QB pour cette ligne (persistée au +/-)
   * @param {number} [includedReductionPercent=100] — % de réduction catalogue sur les unités couvertes (100 = offert, 75 = paye 25 %)
   * @param {{ skipPriceRefresh?: boolean }} [syncOptions] — évite un refresh catalogue entre sync et `applyNextTierPreviews`.
   */
  applyQuantityFromQuantityBreakOffer(quantity, offerBundleQty, includedReductionPercent, syncOptions = {}) {
    delete this.dataset.offerPreviewUnitPayCents;
    delete this.dataset.offerPreviewUnitCompareCents;
    delete this.dataset.offerPreviewReductionPercent;
    delete this.dataset.offerPreviewBundleQty;

    const q = Math.min(MAX_CROSS_SELL_QTY, Math.max(0, quantity));
    const bundleAllocation = Math.min(q, Math.max(0, offerBundleQty));

    if (q > 0 && bundleAllocation > 0) {
      this.dataset.offerBundleMaxQty = String(bundleAllocation);
      const rp =
        includedReductionPercent !== undefined && includedReductionPercent !== null
          ? Math.min(100, Math.max(0, parseInt(String(includedReductionPercent), 10) || 0))
          : 100;
      this.dataset.offerIncludedReductionPercent = String(rp);
    } else {
      delete this.dataset.offerBundleMaxQty;
      delete this.dataset.offerIncludedReductionPercent;
    }

    delete this.dataset.syncedOfferQuantity;

    if (this.#isQuantitySelectorMode()) {
      if (q > 0) {
        this.button?.setAttribute('hidden', '');
        this.quantitySelector?.removeAttribute('hidden');
        this.#setSelectedQuantity(q, syncOptions);
      } else {
        this.#setSelectedQuantity(0, syncOptions);
        this.button?.removeAttribute('hidden');
        this.quantitySelector?.setAttribute('hidden', '');
      }

      if (!syncOptions.skipPriceRefresh) {
        document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
      }
      return;
    }

    if (q > 0) {
      this.dataset.syncedOfferQuantity = String(q);

      this.#forceClassicButtonActive(true);
    } else {
      delete this.dataset.syncedOfferQuantity;
      this.#forceClassicButtonActive(false);
    }

    if (!syncOptions.skipPriceRefresh) {
      this.#refreshMarginalPriceDisplay();
      document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
    }
  }

  #forceClassicButtonActive(active) {
    if (!this.button) return;

    if (active) {
      this.button.classList.add('button--active');
      this.button.classList.remove('button--secondary');
    } else {
      this.button.classList.remove('button--active');
      this.button.classList.add('button--secondary');
    }
  }

  #setSelectedQuantity(value, syncOptions = {}) {
    const q = Math.min(MAX_CROSS_SELL_QTY, Math.max(0, value));

    this.dataset.selectedQuantity = String(q);
    this.classList.toggle('toggle-cross-sell__product--added', q > 0);

    if (this.qtyValue && q > 0) {
      this.qtyValue.textContent = String(q);
    }

    if (q === 0 && this.#isQuantitySelectorMode()) {
      this.button?.removeAttribute('hidden');
      this.quantitySelector?.setAttribute('hidden', '');
    }

    if (!syncOptions.skipPriceRefresh) {
      this.#refreshMarginalPriceDisplay();
    }
  }

  /** Nettoie l’état offre de CETTE ligne + réapplique l’aperçu palier suivant. */
  #syncQuantityBreaksAfterOfferLineRemoved() {
    delete this.dataset.offerBundleMaxQty;
    delete this.dataset.offerIncludedReductionPercent;
    delete this.dataset.syncedOfferQuantity;

    const form = this.closest('product-form');
    const isGiftStyle = Boolean(this.closest('.toggle-cross-sell--gift'));

    if (!isGiftStyle) {
      const qb = form?.querySelector('[data-ref="quantity-breaks"]');

      if (qb != null && typeof qb.syncToggleCrossSellWithSelectedTier === 'function') {
        qb.syncToggleCrossSellWithSelectedTier();
        return;
      }
    }

    this.closest('toggle-cross-sell-list')?.applyNextTierPreviews?.();
    queueMicrotask(() => {
      this.#refreshMarginalPriceDisplay();
      document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
    });
  }

  /** Après décochée / retrait bouton classique. */
  #afterToggleOfferRemoved() {
    this.#syncQuantityBreaksAfterOfferLineRemoved();
  }

  checkboxChanged() {
    if (this.checkbox != null && this.checkbox.checked) {
      this.#promotePreviewOfferToBundleIfNeeded();
      this.#refreshMarginalPriceDisplay();
      document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
      return;
    }

    if (this.checkbox != null && !this.checkbox.checked) {
      this.#afterToggleOfferRemoved();
      return;
    }

    this.#refreshMarginalPriceDisplay();
    document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
  }

  /**
   * @param {boolean} [active] — forcé ; sinon bascule l’état du bouton (clic sans argument).
   */
  toggleAddButton(active) {
    const wasActive = this.button?.classList.contains('button--active') ?? false;
    let willBeActive;
    if (active === true) {
      willBeActive = true;
    } else if (active === false) {
      willBeActive = false;
    } else {
      willBeActive = !wasActive;
    }

    /* Retrait : mettre le bouton en état « non actif » AVANT la synchro QB / aperçu palier suivant.
       Sinon `#refreshMarginalPriceDisplay` croit encore qty≥1 (bouton--actif + syncedOfferQuantity effacé → fallback '1')
       et la branche aperçu `qty === 0` ne s’exécute jamais. */
    if (willBeActive) {
      this.#promotePreviewOfferToBundleIfNeeded();
      this.button.classList.add('button--active');
      this.button.classList.remove('button--secondary');
      this.#refreshMarginalPriceDisplay();
      document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());
    } else {
      this.button.classList.remove('button--active');
      this.button.classList.add('button--secondary');
      this.#afterToggleOfferRemoved();
    }
  }

  variantChanged() {
    const optionSelected = this.select.options[this.select.selectedIndex];

    delete this.dataset.offerBundleMaxQty;
    delete this.dataset.offerIncludedReductionPercent;
    delete this.dataset.syncedOfferQuantity;

    this.updateProductPrice(optionSelected);
    this.updateProductImage(optionSelected);
    this.updateVariantId(optionSelected);

    if (this.checkbox != null) this.checkbox.checked = true;

    if (this.#isQuantitySelectorMode()) {
      this.#setSelectedQuantity(0);
      this.button?.removeAttribute('hidden');
      this.quantitySelector?.setAttribute('hidden', '');
    } else if (this.button != null) {
      this.toggleAddButton(true);
    }

    document.dispatchEvent(new ToggleCrossSellsUpdatedEvent());

    requestAnimationFrame(() => {
      this.closest('toggle-cross-sell-list')?.applyNextTierPreviews?.();
    });
  }

  clearNextTierOfferPreview(options = {}) {
    const had = this.dataset.offerPreviewUnitPayCents != null;
    delete this.dataset.offerPreviewUnitPayCents;
    delete this.dataset.offerPreviewUnitCompareCents;
    delete this.dataset.offerPreviewReductionPercent;
    delete this.dataset.offerPreviewBundleQty;
    if (had && !options.skipRefresh) this.#refreshMarginalPriceDisplay();
  }

  refreshMarginalPriceDisplay() {
    this.#refreshMarginalPriceDisplay();
  }

  /**
   * @param {HTMLElement} span — `quantity-break-included-item` du palier cible
   */
  setNextTierOfferPreview(span) {
    const eff = parseInt(span.dataset.price, 10);
    const cmp = parseInt(span.dataset.compareAtPrice, 10);
    const rawRp = span.getAttribute('data-included-price-reduction-percent');
    const rp = parseInt(String(rawRp), 10);
    const qty = parseInt(span.dataset.quantity, 10) || 1;

    if (Number.isNaN(eff) || rawRp === '' || Number.isNaN(rp)) return;

    this.dataset.offerPreviewUnitPayCents = String(eff);
    this.dataset.offerPreviewUnitCompareCents = String(!Number.isNaN(cmp) ? cmp : eff);
    this.dataset.offerPreviewReductionPercent = String(Math.min(100, Math.max(0, rp)));
    this.dataset.offerPreviewBundleQty = String(Math.max(1, qty));
    this.#refreshMarginalPriceDisplay();
  }

  #promotePreviewOfferToBundleIfNeeded() {
    if (this.dataset.offerPreviewReductionPercent == null) return;

    const rp = parseInt(this.dataset.offerPreviewReductionPercent, 10);
    const bq = parseInt(this.dataset.offerPreviewBundleQty || '1', 10);

    this.dataset.offerBundleMaxQty = String(Math.max(1, Number.isNaN(bq) ? 1 : bq));
    this.dataset.offerIncludedReductionPercent = String(
      Math.min(100, Math.max(0, Number.isNaN(rp) ? 100 : rp))
    );

    delete this.dataset.offerPreviewUnitPayCents;
    delete this.dataset.offerPreviewUnitCompareCents;
    delete this.dataset.offerPreviewReductionPercent;
    delete this.dataset.offerPreviewBundleQty;
  }

  /** Aperçu palier suivant : barré dans `<s class="product-configurator__crosssell-item-compare">` (hors snippet `price`). */
  #applyStrikeAndPayUnitDisplay(unitPayCents, strikeCompareCents) {
    if (!this.price) return;

    this.#removeInjectedStrikePriceEl();

    const previewCompareEl = this.querySelector('[data-ref="toggle-cross-sell-offer-compare-preview"]');

    const unitPay = parseInt(String(unitPayCents), 10) || 0;

    let strikeFromOffer = parseInt(String(strikeCompareCents), 10);
    if (Number.isNaN(strikeFromOffer)) strikeFromOffer = 0;

    const unitCatalog = parseInt(this.dataset.price, 10) || 0;
    const compareRaw = this.dataset.compareAtPrice;
    const unitVariantCompare =
      compareRaw != null && compareRaw !== '' ? parseInt(String(compareRaw), 10) : NaN;

    let strikeFromVariant = 0;
    if (!Number.isNaN(unitVariantCompare) && unitVariantCompare > unitPay) {
      strikeFromVariant = unitVariantCompare;
    } else if (unitCatalog > unitPay) {
      strikeFromVariant = unitCatalog;
    }

    let strikeBase = Math.max(strikeFromOffer, strikeFromVariant);
    if (strikeBase < unitPay) strikeBase = unitPay;

    this.price.querySelectorAll('.compare-at-price').forEach((el) => {
      el.innerHTML = '';
      el.setAttribute('hidden', '');
    });

    if (previewCompareEl) {
      if (strikeBase > unitPay && strikeBase > 0) {
        previewCompareEl.innerHTML = formatMoney(strikeBase);
        previewCompareEl.removeAttribute('hidden');
      } else {
        previewCompareEl.innerHTML = '';
        previewCompareEl.setAttribute('hidden', '');
      }
    }

    const priceSpan = this.price.querySelector('.price');
    if (priceSpan) priceSpan.innerHTML = formatMoney(unitPay);
  }

  updateProductPrice(optionSelected) {
    this.#applyVariantOptionToDom(optionSelected);
    this.#refreshMarginalPriceDisplay();
  }

  #applyVariantOptionToDom(optionSelected) {
    const variantPrice = optionSelected.dataset.price;
    const variantCompareAtPrice = optionSelected.dataset.compareAtPrice;

    const priceSpan = this.price?.querySelector('.price');
    if (priceSpan) priceSpan.innerHTML = formatMoney(variantPrice);
    this.dataset.price = variantPrice;

    this.price?.querySelectorAll('.compare-at-price').forEach((el) => {
      if (variantCompareAtPrice != null) {
        el.innerHTML = formatMoney(variantCompareAtPrice);
      } else {
        el.innerHTML = '';
      }
    });

    if (variantCompareAtPrice != null) {
      this.dataset.compareAtPrice = variantCompareAtPrice;
    } else {
      delete this.dataset.compareAtPrice;
    }
  }

  #applySingleVariantDatasetToDom() {
    if (!this.price) return;

    const p = this.dataset.price;
    const c = this.dataset.compareAtPrice;
    const priceSpan = this.price.querySelector('.price');

    if (priceSpan && p != null) priceSpan.innerHTML = formatMoney(p);

    this.price.querySelectorAll('.compare-at-price').forEach((el) => {
      if (c != null && parseInt(c, 10) > parseInt(p, 10)) {
        el.innerHTML = formatMoney(c);
      } else {
        el.innerHTML = '';
      }
    });
  }

  #removeInjectedStrikePriceEl() {
    this.price?.querySelector('[data-ref="toggle-cross-sell-injected-strike"]')?.remove();
  }

  #clearOfferComparePreviewEl() {
    const el = this.querySelector('[data-ref="toggle-cross-sell-offer-compare-preview"]');
    if (!el) return;

    el.innerHTML = '';
    el.setAttribute('hidden', '');
  }

  /** Badge écart prix barré → prix promo (même style que « Offerts »). */
  #updateSavingsBadge() {
    const savingsBadge = this.querySelector('[data-ref="toggle-cross-sell-savings-badge"]');
    if (!savingsBadge) return;

    const price = parseInt(this.dataset.price, 10) || 0;
    const compareRaw = this.dataset.compareAtPrice;
    const compare =
      compareRaw != null && compareRaw !== '' ? parseInt(compareRaw, 10) || price : price;

    if (compare > price) {
      savingsBadge.removeAttribute('hidden');
      savingsBadge.textContent = `-${formatMoney(compare - price)}`;
    } else {
      savingsBadge.setAttribute('hidden', '');
    }
  }

  /**
   * Offre QB : barré + prix marginal selon % réduction (100 % → 0 € + badge « Offerts »).
   * offerBundleMaxQty est conservé au +/- pour retrouver l’offre en baissant la quantité.
   */
  #refreshMarginalPriceDisplay() {
    const badge = this.querySelector('[data-ref="toggle-cross-sell-offer-badge"]');
    const previewPctBadge = this.querySelector('[data-ref="toggle-cross-sell-preview-percent-badge"]');
    if (previewPctBadge) previewPctBadge.setAttribute('hidden', '');

    if (!this.price) {
      this.#updateSavingsBadge();
      return;
    }

    this.#removeInjectedStrikePriceEl();
    this.#clearOfferComparePreviewEl();

    let qty = 0;

    if (this.#isQuantitySelectorMode()) {
      qty = parseInt(this.dataset.selectedQuantity || '0', 10);
    } else if (this.button?.classList.contains('button--active')) {
      const sq = this.dataset.syncedOfferQuantity;
      qty = sq != null && sq !== '' ? parseInt(sq, 10) || 0 : 0;
    } else if (this.checkbox != null && this.checkbox.checked) {
      qty = 1;
    }

    const previewPay = this.dataset.offerPreviewUnitPayCents;
    const bundleMaxActive = parseInt(this.dataset.offerBundleMaxQty || '0', 10) > 0;
    const hasSyncedOfferQty =
      this.dataset.syncedOfferQuantity != null && String(this.dataset.syncedOfferQuantity).trim() !== '';

    if (previewPay != null && !bundleMaxActive && !hasSyncedOfferQty && qty === 0) {
      const unitPay = parseInt(previewPay, 10);
      const unitCmp = parseInt(this.dataset.offerPreviewUnitCompareCents || previewPay, 10);
      if (!Number.isNaN(unitPay)) {
        if (badge) badge.hidden = true;
        this.querySelector('[data-ref="toggle-cross-sell-savings-badge"]')?.setAttribute('hidden', '');

        let pct = parseInt(this.dataset.offerPreviewReductionPercent ?? '', 10);
        if (Number.isNaN(pct) && unitCmp > unitPay && unitCmp > 0) {
          pct = Math.round(((unitCmp - unitPay) / unitCmp) * 100);
        }

        if (!Number.isNaN(pct) && pct > 0 && previewPctBadge) {
          previewPctBadge.textContent = `-${pct}%`;
          previewPctBadge.removeAttribute('hidden');
        }

        this.#applyStrikeAndPayUnitDisplay(unitPay, unitCmp);
        return;
      }
    }

    const bundleMax = parseInt(this.dataset.offerBundleMaxQty || '0', 10);
    const reductionRaw = parseInt(this.dataset.offerIncludedReductionPercent ?? '100', 10);
    const r = Math.min(100, Math.max(0, Number.isNaN(reductionRaw) ? 100 : reductionRaw));
    const atBundleExact = qty > 0 && bundleMax > 0 && qty === bundleMax;

    const applyOfferStrikeAndSale = (unitPayCents) => {
      const unitPrice = parseInt(this.dataset.price, 10) || 0;
      const unitCompare =
        this.dataset.compareAtPrice != null ? parseInt(this.dataset.compareAtPrice, 10) : unitPrice;
      const strikeBase = unitCompare > unitPrice ? unitCompare : unitPrice;

      const pricesEl = this.price.querySelector('.prices');
      const priceSpan = this.price.querySelector('.price');
      let compareEls = this.price.querySelectorAll('.compare-at-price');

      if (compareEls.length === 0 && strikeBase > 0 && pricesEl && priceSpan) {
        const strike = document.createElement('span');
        strike.className = 'compare-at-price';
        strike.setAttribute('data-ref', 'toggle-cross-sell-injected-strike');
        pricesEl.insertBefore(strike, priceSpan);
        compareEls = this.price.querySelectorAll('.compare-at-price');
      }

      compareEls.forEach((el) => {
        if (strikeBase > 0) {
          el.innerHTML = formatMoney(strikeBase);
          el.removeAttribute('hidden');
        } else {
          el.innerHTML = '';
          el.setAttribute('hidden', '');
        }
      });

      if (priceSpan) priceSpan.innerHTML = formatMoney(unitPayCents);
    };

    if (atBundleExact && r >= 100) {
      if (badge) badge.hidden = false;
      if (previewPctBadge) previewPctBadge.setAttribute('hidden', '');
      applyOfferStrikeAndSale(0);
      this.#updateSavingsBadge();
      return;
    }

    if (atBundleExact && r > 0 && r < 100) {
      if (badge) badge.hidden = true;
      this.querySelector('[data-ref="toggle-cross-sell-savings-badge"]')?.setAttribute('hidden', '');
      const unitPrice = parseInt(this.dataset.price, 10) || 0;
      const unitPay = centsAfterPercentReduction(unitPrice, r);
      applyOfferStrikeAndSale(unitPay);
      if (previewPctBadge) {
        previewPctBadge.textContent = `-${r}%`;
        previewPctBadge.removeAttribute('hidden');
      }
      return;
    }

    if (badge) badge.hidden = true;

    if (this.select) {
      const opt = this.select.options[this.select.selectedIndex];
      if (opt) this.#applyVariantOptionToDom(opt);
    } else {
      this.#applySingleVariantDatasetToDom();
    }

    this.#updateSavingsBadge();
  }

  updateProductImage(optionSelected) {
    const variantImage = optionSelected.dataset.variantImage;

    if (variantImage != null && this.image != null) {
      this.image.querySelector('img').src = variantImage;
    }
  }

  updateVariantId(optionSelected) {
    const variantId = optionSelected.value;

    if (this.checkbox != null) this.checkbox.dataset.variantId = variantId;
    if (this.button != null) this.button.dataset.variantId = variantId;
  }

  getPrices() {
    let price = 0;
    let compareAtPrice = 0;

    if (this.dataset.price != null) {
      price = parseInt(this.dataset.price);
    }

    if (this.dataset.compareAtPrice != null) {
      compareAtPrice = parseInt(this.dataset.compareAtPrice);
    } else {
      compareAtPrice = parseInt(this.dataset.price);
    }

    return {
      price: price,
      compareAtPrice: compareAtPrice,
    };
  }
}

if (!customElements.get('toggle-cross-sell')) {
  customElements.define('toggle-cross-sell', ToggleCrossSell);
}
